import React, { useEffect, useState } from "react";
import { ExternalLink, ShoppingBag } from "lucide-react";
import { api } from "../../lib/api";

/**
 * PartnerShopsStrip — thin horizontally-scrollable chip row that
 * surfaces every outdoor-shop affiliate partner (Trek Expert, Camping
 * World UK, Absolute Snow, Findra, Teva…). Silent for non-outdoor
 * partners so nothing accidentally leaks in.
 *
 * iter171 — user asked for "all the outdoor shops which I have as
 * affiliate links on the top header part of the page — don't
 * overcrowd, easy to link through". Design choice: horizontal chip
 * row over dropdown — one tap per merchant, no menu discovery, chips
 * scroll on mobile if we ever add a 6th+ partner.
 */

// Explicitly excluded per user request. campsites.co.uk is a booking
// site, not an outdoor gear shop.
const EXCLUDE_SLUGS = new Set(["campsites-co-uk"]);

// Kept small on purpose — brand favicon at 24px is a familiar visual
// anchor without needing a full logo asset per brand.
const faviconFor = (rootUrl) => {
  try {
    const u = new URL(rootUrl);
    return `https://www.google.com/s2/favicons?domain=${u.hostname}&sz=64`;
  } catch { return null; }
};

export default function PartnerShopsStrip() {
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/affiliate/brands").then(({ data }) => {
      if (!alive) return;
      const items = (data.brands || []).filter((b) => !EXCLUDE_SLUGS.has(b.slug));
      setBrands(items);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (brands.length === 0) return null;

  return (
    <div
      className="w-full bg-[#161616] text-white/95 border-b border-white/5"
      data-testid="partner-shops-strip"
      aria-label="Affiliate partner shops"
    >
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-10 py-1.5 flex items-center gap-3">
        <span
          className="hidden sm:inline-flex items-center gap-1.5 shrink-0 text-[10px] mono uppercase tracking-widest text-white/60"
        >
          <ShoppingBag className="w-3 h-3" /> Partner shops
        </span>
        <div
          className="flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-1 min-w-0 -mx-1 px-1"
          data-testid="partner-shops-strip-scroll"
        >
          {brands.map((b) => (
            <a
              key={b.slug}
              href={`/api/r/${b.slug}?clickref=partner-strip`}
              target="_blank"
              rel="sponsored noopener noreferrer"
              title={b.blurb || b.name}
              className="inline-flex items-center gap-1.5 shrink-0 bg-white/5 hover:bg-white/15 border border-white/10 hover:border-white/25 px-2 py-1 text-[11px] sm:text-xs font-semibold transition-colors"
              data-testid={`partner-shop-${b.slug}`}
            >
              {faviconFor(b.root_url) && (
                <img
                  src={faviconFor(b.root_url)}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="w-3.5 h-3.5 object-contain shrink-0 rounded-sm bg-white/95 p-[1px]"
                />
              )}
              <span className="whitespace-nowrap">{b.name}</span>
              <ExternalLink className="w-3 h-3 opacity-60" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
